import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Editor as TipTapEditor, useEditor } from "@tiptap/react";
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import Editor from "components/Editor";
import MergeTagMenuBar from "components/Editor/MergeTag/MergeTagMenuBar";
import { extensions } from "components/Editor/utils";
import { MERGE_TAGS } from "constants/templates";
import { Template } from "features/Influencer/ContactList/schema";

import styles from "./styles";

interface SubjectLineListProps {
  editTemplate: Template;
  setEditTemplate: Dispatch<SetStateAction<Template>>;
  setIsDirty?: Dispatch<SetStateAction<boolean>>;
  evaluating?: boolean;
  // Bumped by the parent to force a re-init from editTemplate (e.g. revision restore).
  restoreSignal?: number;
}

interface Variant {
  id: number;
  html: string;
}

function SubjectVariant({
  index,
  initialHtml,
  editable,
  showNumber,
  canRemove,
  first,
  onChange,
  onFocus,
  onRemove,
}: {
  index: number;
  initialHtml: string;
  editable: boolean;
  showNumber: boolean;
  canRemove: boolean;
  first: boolean;
  onChange: (html: string) => void;
  onFocus: (editor: TipTapEditor) => void;
  onRemove: () => void;
}) {
  const editor = useEditor(
    {
      extensions: extensions({
        isPlain: true,
        suggestionOptions: MERGE_TAGS,
        placeholderText:
          index === 0 ? "Email Subject" : "Try a different angle...",
      }),
      content: initialHtml,
      onUpdate({ editor }) {
        onChange(editor.getHTML());
      },
      onFocus({ editor }) {
        onFocus(editor as TipTapEditor);
      },
    },
    [],
  ) as TipTapEditor;

  useEffect(() => {
    editor?.setEditable(editable, false);
  }, [editor, editable]);

  return (
    <Box sx={[styles.row, first ? styles.firstRow : {}]}>
      <Box sx={styles.field}>
        {showNumber && (
          <Box component="span" sx={styles.numberInside}>
            {index + 1}
          </Box>
        )}
        <Box sx={styles.editorSlot}>
          <Editor
            editor={editor}
            hideMenu
            hasBorder={false}
            mergeTags={MERGE_TAGS}
          />
        </Box>
      </Box>
      {canRemove && (
        <Tooltip title="Remove variant">
          <IconButton size="small" sx={styles.removeButton} onClick={onRemove}>
            <Box component="i" className="fa-regular fa-trash" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

export default function SubjectLineList({
  editTemplate,
  setEditTemplate,
  setIsDirty,
  evaluating = false,
  restoreSignal,
}: SubjectLineListProps) {
  const nextId = useRef(0);
  const makeVariants = useCallback((htmls: string[]): Variant[] => {
    return (htmls.length ? htmls : [""]).map((html) => ({
      id: nextId.current++,
      html,
    }));
  }, []);

  const [variants, setVariants] = useState<Variant[]>(() =>
    makeVariants(editTemplate.subjectVariants ?? [editTemplate.subject ?? ""]),
  );
  const [activeEditor, setActiveEditor] = useState<TipTapEditor | null>(null);

  useEffect(() => {
    setVariants(
      makeVariants(
        editTemplate.subjectVariants ?? [editTemplate.subject ?? ""],
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editTemplate.id, restoreSignal]);

  // Mirror variant #1 into editTemplate.subject (the deprecated backup).
  useEffect(() => {
    const lines = variants.map((variant) => variant.html);
    setEditTemplate((prev) => ({
      ...prev,
      subject: lines[0] ?? "",
      subjectVariants: lines,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variants]);

  const markDirty = () => {
    if (setIsDirty) setIsDirty(true);
  };

  const handleChange = useCallback((id: number, html: string) => {
    setVariants((prev) =>
      prev.map((variant) =>
        variant.id === id ? { ...variant, html } : variant,
      ),
    );
  }, []);

  const handleAdd = () => {
    setVariants((prev) => [...prev, { id: nextId.current++, html: "" }]);
    markDirty();
  };

  const handleRemove = (id: number) => {
    setVariants((prev) => prev.filter((variant) => variant.id !== id));
    markDirty();
  };

  const expanded = variants.length > 1;

  return (
    <Box>
      <Box sx={styles.header}>
        <Box component="span" sx={styles.label}>
          Subject
        </Box>
        {expanded && (
          <Box component="span" sx={styles.rotatingBadge}>
            <Box component="i" className="fa-solid fa-shuffle" />
            Rotating {variants.length}
          </Box>
        )}
      </Box>

      <MergeTagMenuBar
        editor={activeEditor ?? undefined}
        mergeTags={MERGE_TAGS}
        isSubjectLine
      />

      {variants.map((variant, index) => (
        <SubjectVariant
          key={variant.id}
          index={index}
          initialHtml={variant.html}
          editable={!evaluating}
          showNumber={expanded}
          canRemove={expanded}
          first={index === 0}
          onChange={(html) => handleChange(variant.id, html)}
          onFocus={setActiveEditor}
          onRemove={() => handleRemove(variant.id)}
        />
      ))}

      {expanded ? (
        <Box component="span" sx={styles.addAnother} onClick={handleAdd}>
          <Box component="i" className="fa-solid fa-plus" />
          Add another
        </Box>
      ) : (
        <Box sx={styles.cta} onClick={handleAdd}>
          <Box sx={styles.ctaIcon}>
            <Box component="i" className="fa-solid fa-plus" />
          </Box>
          <Box>
            <Typography sx={styles.ctaTitle}>
              Test another subject line
            </Typography>
            <Typography sx={styles.ctaBody}>
              We&apos;ll automatically rotate between them and show you which
              one gets more replies.
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
}
